import { useQuery } from '@apollo/client';
import { T1, ALL_MODULES, getTranslatedText } from '@nile/components';
import { GET_FIELD, AccountClient, SystrayAccountClient } from '@nile/graphql';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  Button,
  ButtonGroup,
  Popover,
  RadioButton,
  TextStyle,
  Tippy
} from '@superops/ui-design-system';
import { Edit } from '@styled-icons/material/Edit';
import React, { useCallback, useState, useEffect } from 'react';
import { isEmpty, isEqual } from 'lodash';
import styled from 'styled-components';
import { EditIcon } from '@nile/components/src/Icons';
import { getCellValue } from '@nile/reports/src/modules/EfficiencyReport/util';
import { useIntl } from 'react-intl';
import { GET_OPTIONS } from '@nile/banyan/src/Graphql/Mutations/ticket';

const CustomField = styled.div`
  min-width: 200px;
  max-width: ${(props) => (props?.fullWidth ? 'unset' : '250px')};
  flex-grow: 1;
  padding-right: ${(props) => !props?.fullWidth && '10px'};
  padding-left: ${(props) => !props?.fullWidth && '10px'};
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }
`;

const Label = styled.div`
  text-align: left;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 10px;
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;

const CField = styled.div`
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

function AsyncRadioNew(props) {
  const {
    field,
    ticket,
    defaultValue,
    updateSelection,
    editable,
    value,
    inlineEdit,
    disableSelection,
    isMandatory,
    data: rawData,
    systray = false
  } = props;

  const intl = useIntl();
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const CLIENT = systray ? SystrayAccountClient : AccountClient;

  const { data, loading, error } = useQuery(GET_FIELD, {
    client: CLIENT,
    variables: {
      input: {
        columnName: field?.columnName || field?.name,
        module: props.module || ALL_MODULES.ticket
      }
    },
    onCompleted({ getField }) {
      const radioOptions = getField?.options || getField?.fieldOptions;
      setOptions(radioOptions);
    }
  });

  const updateSelectedField = (checked, newValue) => {
    updateSelection(newValue || null);
  };

  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });

  const [updatedValue, setUpdatedValue] = useState(value || null);

  useEffect(() => {
    if (!isEqual(updatedValue, value)) {
      setUpdatedValue(value);
    }
  }, [value]);

  const updateUpdatedValue = (val) => {
    setUpdatedValue(val || null);
  };

  const displayVal = value;
  const activator = <EditIcon onClick={() => togglePopoverActive()} />;

  const getTag = () => {
    return (
      <CustomField className={`cf-wrapper `}>
        <Label className="cf-label">
          {field.label || ''}{' '}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
        <div
          className={`flex col ${props.error ? 'cf-error-state' : ''}`}
          key={field.name}
        >
          {data?.getField?.fieldOptions?.map((item) => {
            return (
              <RadioButton
                disabled={disableSelection}
                label={item?.value}
                id={`radio-${item?.value}-${data?.getField?.columnName ||
                  data?.getField?.name}`}
                name={`radio-${item?.value}-${data?.getField?.columnName ||
                  data?.getField?.name}`}
                checked={updatedValue === item?.value}
                onChange={() => updateUpdatedValue(item?.value)}
              />
            );
          })}
        </div>
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                setUpdatedValue('');
              }}
            >
              Cancel
            </Button>
            <Button
              primary
              onClick={() => {
                updateSelectedField('', updatedValue);
                togglePopoverActive();
              }}
            >
              Save
            </Button>
          </ButtonGroup>
        </div>
      </CustomField>
    );
  };

  const filterOptions = (parentField) => {
    setOptionsLoading(true);
    CLIENT.query({
      query: GET_OPTIONS,
      variables: {
        input: {
          field: { id: data?.getField?.id, module: data?.getField?.module },
          parentOptionValue: { value: parentField }
        }
      }
    })
      .then((res) => {
        const opts = res?.data?.getOptions?.map((el) => ({
          name: el?.value,
          value: el?.value,
          label: el?.value,
          parentOption: el?.parentOption
        }));
        setOptions(opts);
      })
      .finally(() => setOptionsLoading(false));
  };

  useEffect(() => {
    const parentField = data?.getField?.parentField;
    if (parentField) {
      const key =
        data?.getField?.parentField?.fieldCategory === 'CUSTOM'
          ? `customFields.${data?.getField?.parentField?.columnName ||
              field?.parentField?.columnName ||
              field?.nestedTo}`
          : data?.getField?.parentField?.columnName ||
            field?.parentField?.columnName ||
            field?.nestedTo;
      const parentValue = getCellValue(rawData, key);
      const prntValue =
        typeof parentValue === 'object'
          ? parentValue?.name || parentValue?.id
          : parentValue;
      if (isEmpty(prntValue)) {
        setOptions([
          {
            value: getTranslatedText(
              intl,
              'common.admin.depedant.field.choose.parent'
            )
          }
        ]);
      } else {
        filterOptions(prntValue);
      }
    }
  }, [rawData, data]);

  if (loading || optionsLoading) return <div>loading...</div>;
  if (inlineEdit) {
    if (!editable && field?.isDependent) {
      return (
        <Tippy
          title={`${getTranslatedText(
            intl,
            'common.ticket.field.dependant.title',
            {
              name: field?.parent?.label
                ? getTranslatedText(intl, field?.parent?.label)
                : ''
            }
          )}`}
          duration={250}
          delay={100}
          placement="bottom-start"
          arrow={false}
          theme="dark"
          appendTo={document.body}
        >
          <CField className="disable-actions">
            <Wrap>
              <div className="ellipsis" title={displayVal}>
                {displayVal || (
                  <TextStyle variation="subdued">
                    {getTranslatedText(intl, 'app.noData.message')}
                  </TextStyle>
                )}
              </div>
            </Wrap>
          </CField>
        </Tippy>
      );
    }
    return !popoverActive ? (
      <CField>
        <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
          <div className="ellipsis" title={displayVal}>
            {displayVal || (
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'app.noData.message')}
              </TextStyle>
            )}
          </div>
          {editable && <div className="edit-actions">{activator}</div>}
        </Wrap>
      </CField>
    ) : (
      <CField>
        <Wrap className="editable-cell">
          <div className="ellipsis" title={displayVal}>
            {displayVal || (
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'app.noData.message')}
              </TextStyle>
            )}
          </div>
          <Popover
            activator={activator}
            active={popoverActive}
            onClose={() => {
              // togglePopoverActive()
            }}
          >
            {getTag()}
          </Popover>
        </Wrap>
      </CField>
    );
  }
  return (
    <CustomField fullWidth={props?.fullWidth} className="cf-wrapper">
      <Label className="cf-label">
        {field.label || ''}
        {isMandatory && (
          <>
            <span className="mandatory-star">*</span>
          </>
        )}
      </Label>
      <div
        className={`flex col ${props.error ? 'cf-error-state' : ''}`}
        key={field.name}
      >
        {options?.map((item) => {
          return (
            <RadioButton
              label={item?.value}
              id={`radio-${item?.value}-${data?.getField?.columnName}`}
              name={`radio-${item?.value}-${data?.getField?.columnName}`}
              checked={value === item?.value}
              onChange={() => updateSelection(item?.value)}
              disabled={disableSelection}
            />
          );
        })}
      </div>
    </CustomField>
  );
}

export default AsyncRadioNew;
